import axios from "axios";
import { authApi } from "./authApi";
import authService from "../services/authService";

// Создаем глобальную переменную для хранения функции обновления токена
let updateTokenFunction: ((token: string, refreshToken: string) => void) | null = null;

// Функция для установки функции обновления токена
export const setUpdateTokenFunction = (fn: (token: string, refreshToken: string) => void) => {
  updateTokenFunction = fn;
};

// Функция для установки состояния обновления токена
let setRefreshingTokenStateFunction: ((isRefreshing: boolean) => void) | null = null;

export const setRefreshingTokenState = (fn: (isRefreshing: boolean) => void) => {
  setRefreshingTokenStateFunction = fn;
};

// Переменная для блокировки параллельных запросов на обновление токена
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const API_BASE_URL = "https://parallel.one/api/v1";
//const API_BASE_URL = "http://localhost:10025/api/v1";

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Добавляем перехватчик для добавления токена авторизации
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Добавляем перехватчик для обрабcотки 401 ошибки
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Если ошибка 401 и это не повторный запрос и это не запрос на обновление токена
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/identity/refreshToken')) {
      originalRequest._retry = true;

      // Проверяем, есть ли токен
      const token = localStorage.getItem('token');
      if (!token) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        authService.clearTokens();
        authService.openAuthDialog('login');
        return Promise.reject(error);
      }

      // Если уже идет обновление токена, добавляем запрос в очередь
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      // Проверяем, что функция обновления токена установлена
      if (!updateTokenFunction) {
        console.warn('Update token function not set, cannot refresh token');
        authService.clearTokens();
        authService.openAuthDialog('login');
        return Promise.reject(error);
      }

      isRefreshing = true;  

      // Устанавливаем состояние обновления токена только если оно еще не установлено
      if (setRefreshingTokenStateFunction) {
        setRefreshingTokenStateFunction(true);
      }

      try {
        // Пытаемся обновить токен
        const response = await axios.post(`${API_BASE_URL}/identity/refreshToken`, null, {
          params: { token: refreshToken },
          headers: { "Content-Type": "application/json" }
        });
        
        const responseData = response.data;
        
        if (responseData.success && responseData.accessToken) {
          // Сохраняем новые токены
          localStorage.setItem('token', responseData.accessToken);
          if (responseData.refreshToken) {
            localStorage.setItem('refreshToken', responseData.refreshToken);
          }
          
          // Обновляем состояние пользователя в контексте
          if (updateTokenFunction) {
            updateTokenFunction(responseData.accessToken, responseData.refreshToken || '');
          }
          
          // Обрабатываем очередь запросов
          processQueue(null, responseData.accessToken);
          
          // Обновляем заголовок и повторяем запрос
          originalRequest.headers.Authorization = `Bearer ${responseData.accessToken}`;
          
          return api(originalRequest);
                  } else {
            processQueue(new Error(responseData.message || 'Ошибка обновления токена'));
            authService.clearTokens();
            authService.openAuthDialog('login');
            return Promise.reject(error);
          }
        } catch (refreshError) {
          console.error('Refresh token error:', refreshError);
          processQueue(refreshError);
          authService.clearTokens();
          authService.openAuthDialog('login');
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
          // Сбрасываем состояние обновления токена
          if (setRefreshingTokenStateFunction) {
            setRefreshingTokenStateFunction(false);
          }
        }
      } else if (error.response?.status === 401) {
        // Если это повторная 401 ошибка или ошибка при обновлении токена
        authService.clearTokens();
        authService.openAuthDialog('login');
        return Promise.reject(error);
      }

    return Promise.reject(error);
  }
);

export default api;
